// src/utils/coordinateUtils.js

/**
 * Convierte grados, minutos y segundos (DMS) a grados decimales.
 */
export const dmsToDecimal = (degrees, minutes, seconds, direction) => {
    let decimal = parseFloat(degrees) + parseFloat(minutes) / 60 + parseFloat(seconds) / 3600;
    if (['S', 'W', 's', 'w'].includes(direction.toUpperCase())) {
        decimal = -decimal;
    }
    return decimal;
};

/**
 * Valida si un par de coordenadas latitud/longitud están dentro de los rangos válidos.
 * Latitud: [-90, 90]
 * Longitud: [-180, 180]
 */
export const isValidCoordinates = (lat, lng) => {
    if (lat === null || lng === null || isNaN(lat) || isNaN(lng)) return false;
    return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
};

/**
 * Detecta si el usuario ingresó la longitud antes que la latitud (muy común al copiar y pegar),
 * especialmente diseñado para Perú (Sullana) y América Latina, y las intercambia automáticamente.
 */
export const autoSwapCoords = (lat, lng) => {
    // Caso general 1: La latitud excede 90, pero es una longitud válida, y la longitud es una latitud válida
    if (Math.abs(lat) > 90 && Math.abs(lat) <= 180 && Math.abs(lng) <= 90) {
        return [lng, lat];
    }

    // Caso general 2: Latitud es exactamente 0 pero longitud tiene coordenadas invertidas
    // Caso específico regional (Perú): 
    // Típica Longitud de Perú: [-83, -68] (se ingresa por error como latitud)
    // Típica Latitud de Perú: [-18, 0] (se ingresa por error como longitud)
    if (lat >= -90 && lat <= -45 && lng >= -25 && lng <= 5) {
        return [lng, lat]; // Retorna [lat, lng] corregidos
    }

    return [lat, lng];
};

/**
 * Parsea un input de coordenadas (String, Objeto o Array) y devuelve un array [lat, lng] de números.
 * Soporta:
 *  - Objetos point de PostgreSQL: { x: lng, y: lat }
 *  - Arrays directos: [lat, lng] o [lng, lat]
 *  - Strings con punto decimal: "-12.0464, -77.0428"
 *  - Strings con coma decimal: "-12,0464, -77,0428" o "-12,0464; -77,0428"
 *  - Formato DMS: 4°35'56.2"S 81°16'18.8"W o 12° 2' 47.04" S, 77° 2' 34.08" W
 *  - Enlaces de Google Maps: urls de búsqueda, direcciones o lugar.
 */
export const parseCoordinates = (input) => {
    if (!input) return [null, null];

    try {
        // 1. Si es un array
        if (Array.isArray(input)) {
            if (input.length === 2) {
                const lat = parseFloat(input[0]);
                const lng = parseFloat(input[1]);
                if (!isNaN(lat) && !isNaN(lng)) {
                    return autoSwapCoords(lat, lng);
                }
            }
            return [null, null];
        }

        // 2. Si es un objeto de PostgreSQL ({x: lng, y: lat}) o estándar ({lat, lng})
        if (typeof input === 'object') {
            if (input.x !== undefined && input.y !== undefined) {
                // PostgreSQL: x es longitud, y es latitud
                const lat = parseFloat(input.y);
                const lng = parseFloat(input.x);
                if (!isNaN(lat) && !isNaN(lng)) {
                    return autoSwapCoords(lat, lng);
                }
            }
            if (input.lat !== undefined && input.lng !== undefined) {
                const lat = parseFloat(input.lat);
                const lng = parseFloat(input.lng);
                if (!isNaN(lat) && !isNaN(lng)) {
                    return autoSwapCoords(lat, lng);
                }
            }
            return [null, null];
        }

        // 3. Si es un string
        if (typeof input === 'string') {
            let cleanStr = input.trim();

            // 3.1 Detectar si es una URL de Google Maps
            if (cleanStr.includes('google.com/maps') || cleanStr.includes('maps.google') || cleanStr.includes('maps.app.goo.gl')) {
                // Patrón 1: /@lat,lng,zoom
                const atPattern = /@(-?\d+\.\d+),(-?\d+\.\d+)/;
                const atMatch = cleanStr.match(atPattern);
                if (atMatch) {
                    return autoSwapCoords(parseFloat(atMatch[1]), parseFloat(atMatch[2]));
                }

                // Patrón 2: q=lat,lng o query=lat,lng
                const qPattern = /[?&](?:q|query)=(-?\d+\.\d+),(-?\d+\.\d+)/;
                const qMatch = cleanStr.match(qPattern);
                if (qMatch) {
                    return autoSwapCoords(parseFloat(qMatch[1]), parseFloat(qMatch[2]));
                }

                // Patrón 3: /place/lat,lng
                const placePattern = /\/place\/(-?\d+\.\d+),(-?\d+\.\d+)/;
                const placeMatch = cleanStr.match(placePattern);
                if (placeMatch) {
                    return autoSwapCoords(parseFloat(placeMatch[1]), parseFloat(placeMatch[2]));
                }
            }

            // 3.2 Detectar si es formato DMS (Grados, Minutos, Segundos)
            // Ejemplo: 12° 2' 47.04" S, 77° 2' 34.08" W o 4°35'56.2"S 81°16'18.8"W
            const dmsRegex = /(\d+)\s*°\s*(\d+)\s*'\s*(\d+(?:\.\d+)?)\s*"\s*([NSEWnsew])/g;
            const dmsMatches = [...cleanStr.matchAll(dmsRegex)];

            if (dmsMatches.length === 2) {
                const lat = dmsToDecimal(dmsMatches[0][1], dmsMatches[0][2], dmsMatches[0][3], dmsMatches[0][4]);
                const lng = dmsToDecimal(dmsMatches[1][1], dmsMatches[1][2], dmsMatches[1][3], dmsMatches[1][4]);
                if (!isNaN(lat) && !isNaN(lng)) {
                    return autoSwapCoords(lat, lng);
                }
            }

            // 3.3 Procesar strings de coordenadas numéricas limpias
            // Eliminar paréntesis, corchetes o llaves si existen
            cleanStr = cleanStr.replace(/[()\[\]{}]/g, '').trim();

            let parts = [];
            if (cleanStr.includes(';')) {
                // Formato: lat; lng con coma o punto decimal
                parts = cleanStr.split(';').map(p => p.trim());
            } else if (cleanStr.includes(', ')) {
                // Separado por coma y espacio (ej. "-12,0464, -77,0428" -> split por ", ")
                parts = cleanStr.split(', ').map(p => p.trim());
            } else {
                // Separado por coma simple (ej. "-12.0464,-77.0428")
                parts = cleanStr.split(',').map(p => p.trim());
            }

            // Reemplazar coma por punto decimal si existe para parsear correctamente
            parts = parts.map(part => part.replace(',', '.'));

            if (parts.length === 2) {
                const lat = parseFloat(parts[0]);
                const lng = parseFloat(parts[1]);

                if (!isNaN(lat) && !isNaN(lng)) {
                    return autoSwapCoords(lat, lng);
                }
            }
        }
    } catch (e) {
        console.error('Error al parsear coordenadas:', e, input);
    }

    return [null, null];
};

/**
 * Formatea coordenadas para guardarlas o mostrarlas de forma amigable (lat, lng).
 */
export const formatCoordinates = (lat, lng) => {
    if (lat === null || lng === null || isNaN(lat) || isNaN(lng)) return '';
    return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
};